import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import {
  CreditCard,
  Download,
  Eye,
  Globe,
  MousePointer,
  RefreshCw,
  Smartphone
} from 'lucide-react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import './Campaigns.css';

const SOURCE_OPTIONS = [
  { value: 'all', label: 'Все источники' },
  { value: 'homepage', label: 'Главная balletpost.com' },
  { value: 'studio4_guatemala', label: 'Studio 4 Guatemala' }
];

const RANGES = [
  { id: '24h', label: '24ч' },
  { id: '7d', label: '7 дн' },
  { id: '30d', label: '30 дн' }
];

const TOOLTIP_STYLE = {
  backgroundColor: 'rgba(16, 16, 26, 0.96)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '10px',
  color: '#fff',
  fontSize: '12px',
  boxShadow: '0 12px 32px rgba(0,0,0,0.45)'
};

function formatCount(value) {
  return Number(value || 0).toLocaleString('ru-RU');
}

function conversion(from, to) {
  if (!from) return '—';
  return `${Math.round((to / from) * 1000) / 10}%`;
}

function seriesFor(range, byHour, byDay) {
  if (range === '24h') return byHour;
  if (range === '7d') return byDay.slice(-7);
  return byDay;
}

function sumKey(rows, key) {
  return rows.reduce((sum, row) => sum + Number(row[key] || 0), 0);
}

function RangeSwitch({ value, onChange }) {
  return (
    <div className="track-range" role="tablist">
      {RANGES.map((range) => (
        <button
          key={range.id}
          type="button"
          role="tab"
          aria-selected={value === range.id}
          className={value === range.id ? 'on' : ''}
          onClick={() => onChange(range.id)}
        >
          {range.label}
        </button>
      ))}
    </div>
  );
}

function TrackChart({
  title,
  hint,
  total,
  range,
  onRange,
  data,
  areas,
  height = 260
}) {
  const hasData = data.some((row) => areas.some((area) => Number(row[area.key] || 0) > 0));

  return (
    <section className="track-chart-card">
      <div className="track-chart-head">
        <div>
          <h3>{title}</h3>
          <p>
            <strong>{formatCount(total)}</strong>
            <span> за {range === '24h' ? '24 часа' : range === '7d' ? '7 дней' : '30 дней'}</span>
            {hint ? <span className="track-chart-hint"> · {hint}</span> : null}
          </p>
        </div>
        <RangeSwitch value={range} onChange={onRange} />
      </div>
      <div className="track-chart-body">
        {hasData ? (
          <ResponsiveContainer width="100%" height={height}>
            <AreaChart data={data} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
              <defs>
                {areas.map((area) => (
                  <linearGradient key={area.key} id={`fill-${area.key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={area.color} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={area.color} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="label" stroke="rgba(255,255,255,0.35)" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis stroke="rgba(255,255,255,0.35)" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} width={40} />
              <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={{ color: '#cbd5e1' }} />
              {areas.length > 1 ? (
                <Legend
                  wrapperStyle={{ fontSize: 12, color: '#94a3b8', paddingTop: 8 }}
                  iconType="circle"
                />
              ) : null}
              {areas.map((area) => (
                <Area
                  key={area.key}
                  type="monotone"
                  dataKey={area.key}
                  name={area.name}
                  stroke={area.color}
                  strokeWidth={2.2}
                  fill={`url(#fill-${area.key})`}
                  dot={false}
                  activeDot={{ r: 4, strokeWidth: 0 }}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="track-empty">Пока нет событий за этот период</div>
        )}
      </div>
    </section>
  );
}

function Campaigns() {
  const [campaign, setCampaign] = useState('all');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [visitRange, setVisitRange] = useState('24h');
  const [clickRange, setClickRange] = useState('7d');
  const [storeRange, setStoreRange] = useState('30d');
  const [payRange, setPayRange] = useState('7d');

  const loadStats = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/campaigns/stats', { params: { campaign } });
      if (res.data.success) setStats(res.data.data);
    } catch (error) {
      console.error('Ошибка загрузки трекеров:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign]);

  const byHour = stats?.byHour || [];
  const byDay = stats?.byDay || [];

  const visitsSeries = useMemo(() => seriesFor(visitRange, byHour, byDay), [visitRange, byHour, byDay]);
  const clicksSeries = useMemo(() => seriesFor(clickRange, byHour, byDay), [clickRange, byHour, byDay]);
  const storesSeries = useMemo(() => seriesFor(storeRange, byHour, byDay), [storeRange, byHour, byDay]);
  const paySeries = useMemo(() => seriesFor(payRange, byHour, byDay), [payRange, byHour, byDay]);

  if (loading && !stats) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <span>Загрузка трекеров...</span>
      </div>
    );
  }

  const visits = stats?.visits || 0;
  const uniqueVisits = stats?.uniqueVisits || 0;
  const storeClicks = stats?.storeClicks || 0;
  const uniqueStoreClicks = stats?.uniqueStoreClicks || 0;
  const paidActive = stats?.paidActive || 0;
  const stores = stats?.stores || { appstore: 0, googleplay: 0, web: 0 };

  return (
    <div className="trackers-page fade-in">
      <div className="track-header">
        <div>
          <h1 className="page-title">Трекеры</h1>
          <p className="track-subtitle">
            Визиты лендингов и клики по кнопкам магазинов. Установки и покупки считают App Store и Google Play.
          </p>
        </div>
        <div className="track-header-actions">
          <select
            className="input select track-source"
            value={campaign}
            onChange={(e) => setCampaign(e.target.value)}
          >
            {SOURCE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <button className="btn btn-secondary" onClick={loadStats} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'spin' : ''} />
            Обновить
          </button>
        </div>
      </div>

      <div className="track-kpis">
        <article className="track-kpi accent-blue">
          <div className="track-kpi-icon"><Eye size={20} /></div>
          <span className="track-kpi-label">Лендинг</span>
          <strong>{formatCount(visits)}</strong>
          <span className="track-kpi-meta">{formatCount(uniqueVisits)} уникальных</span>
        </article>
        <article className="track-kpi accent-amber">
          <div className="track-kpi-icon"><MousePointer size={20} /></div>
          <span className="track-kpi-label">Магазины</span>
          <strong>{formatCount(storeClicks)}</strong>
          <span className="track-kpi-meta">{formatCount(uniqueStoreClicks)} уник. · CTR {conversion(visits, storeClicks)}</span>
        </article>
        <article className="track-kpi accent-violet">
          <div className="track-kpi-icon"><Download size={20} /></div>
          <span className="track-kpi-label">Установки</span>
          <strong>—</strong>
          <span className="track-kpi-meta">Считают кабинеты магазинов</span>
        </article>
        <article className="track-kpi accent-green">
          <div className="track-kpi-icon"><CreditCard size={20} /></div>
          <span className="track-kpi-label">Оплата</span>
          <strong>{formatCount(storeClicks)}</strong>
          <span className="track-kpi-meta">
            Клики по кнопкам{paidActive ? ` · ${formatCount(paidActive)} активных подписок` : ''}
          </span>
        </article>
      </div>

      <div className="track-store-pills">
        <div className="track-pill">
          <Smartphone size={16} />
          <span>App Store</span>
          <strong>{formatCount(stores.appstore)}</strong>
        </div>
        <div className="track-pill">
          <Smartphone size={16} />
          <span>Google Play</span>
          <strong>{formatCount(stores.googleplay)}</strong>
        </div>
        <div className="track-pill">
          <Globe size={16} />
          <span>Web</span>
          <strong>{formatCount(stores.web)}</strong>
        </div>
      </div>

      <div className="track-charts-grid">
        <TrackChart
          title="Переходы на лендинг"
          range={visitRange}
          onRange={setVisitRange}
          data={visitsSeries}
          total={sumKey(visitsSeries, 'visits')}
          areas={[{ key: 'visits', name: 'Визиты', color: '#3b82f6' }]}
        />
        <TrackChart
          title="Переходы в магазины"
          range={clickRange}
          onRange={setClickRange}
          data={clicksSeries}
          total={sumKey(clicksSeries, 'store_clicks')}
          areas={[{ key: 'store_clicks', name: 'Клики', color: '#f59e0b' }]}
        />
      </div>

      <TrackChart
        title="Клики по магазинам"
        hint="App Store, Google Play и web"
        range={storeRange}
        onRange={setStoreRange}
        data={storesSeries}
        total={sumKey(storesSeries, 'store_clicks')}
        height={280}
        areas={[
          { key: 'appstore', name: 'App Store', color: '#a78bfa' },
          { key: 'googleplay', name: 'Google Play', color: '#22d3ee' },
          { key: 'web', name: 'Web', color: '#f59e0b' }
        ]}
      />

      <TrackChart
        title="Оплата — клики с лендинга"
        hint="IAP считают магазины"
        range={payRange}
        onRange={setPayRange}
        data={paySeries}
        total={sumKey(paySeries, 'store_clicks')}
        height={240}
        areas={[{ key: 'store_clicks', name: 'Клики по кнопкам', color: '#10b981' }]}
      />
    </div>
  );
}

export default Campaigns;
